/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	Input,
	SimpleChanges,
	OnChanges,
	Output,
	EventEmitter,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as strings from '@touchplan/strings';
import { IHoverOverUserPreferences } from 'src/app/services/hover-over-preferences.service';
import { Ticket } from '@touchplan/common';
import {
	CONFIGURATION_LIST,
	HoverSettingType,
	IHoverOverContentOption,
} from 'src/app/common/utility-components/hover-over-content/hover-over-content-configuration-list';
import { parseName } from 'src/app/utils/parse-name';

interface ITicketHoverOption {
	label: string;
	data: string;
}

@Component({
	selector: 'ticket-hover-contents',
	templateUrl: './ticket-hover-contents.component.html',
	styleUrls: ['./ticket-hover-contents.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketHoverContentsComponent implements OnChanges {
	@Input() private readonly ticketData: Readonly<Ticket>;
	@Input() public settingsData: Partial<IHoverOverUserPreferences>;
	@Input() public imageAttachmentsCount: number;
	@Input() public isLoadingAttachmentsData: boolean;
	@Input() public hoverOverCustomFieldsData: ITicketHoverOption[] = [];
	@Output() public turnOffHoverOver: EventEmitter<void> = new EventEmitter();
	@Output() public showHoverOverSettings: EventEmitter<void> = new EventEmitter();

	private tooltipContentOptions: IHoverOverContentOption[] = CONFIGURATION_LIST;

	public AppStrings = strings;

	public hoverOverData$ = new BehaviorSubject<ITicketHoverOption[]>([]);

	public onTurnOffHoverOver(): void {
		this.turnOffHoverOver.next();
	}

	public onShowHoverOverSettings(): void {
		this.showHoverOverSettings.next();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.settingsData || changes.ticketData || changes.hoverOverCustomFieldsData) {
			this.hoverOverData$.next(
				this.getContentForHover(this.settingsData, this.ticketData, this.hoverOverCustomFieldsData)
			);
		}
	}

	private getContentForHover(
		settings: Partial<IHoverOverUserPreferences>,
		ticket: Readonly<Ticket>,
		customFieldsData: ITicketHoverOption[]
	): ITicketHoverOption[] {
		let ticketData = [];

		if (!!settings && !!ticket) {
			Object.entries(settings).forEach(([key, value]) => {
				if (!value) {
					return false;
				}

				const optionValue = this.tooltipContentOptions.find((option) => option.propertyName === key);
				if (
					optionValue &&
					key !== HoverSettingType.DisplayImageAttachmentsCount &&
					key !== HoverSettingType.DisplayAllCustomFields
				) {
					const optionData = optionValue.ticketData(ticket);

					if (optionData) {
						ticketData.push({
							label: optionValue.label,
							data: typeof optionData === 'object' ? parseName(optionData) : optionData,
						});
					}
				} else if (key === HoverSettingType.DisplayImageAttachmentsCount) {
					ticketData.push({}); // this handles the exceptional case for showing Image Attachments Count when no other data must be displayed
				}
			});

			if (customFieldsData?.length) {
				ticketData = [...ticketData, ...customFieldsData];
			}
		}

		return ticketData;
	}
}
